import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';

export type CallState = 'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnected';

export interface TwilioCallInfo {
  to: string;
  contactName?: string;
  claimId?: string;
  contactId?: string;
  phoneLineId?: string;
}

export interface UseTwilioDeviceReturn {
  /** Current state of the device registration */
  isReady: boolean;
  /** Current call state */
  callState: CallState;
  /** Active call info (who we're calling) */
  activeCallInfo: TwilioCallInfo | null;
  /** Duration in seconds since call connected */
  callDuration: number;
  /** Whether mic is muted */
  isMuted: boolean;
  /** Error message if any */
  error: string | null;
  /** Make an outbound call via browser */
  makeCall: (params: TwilioCallInfo) => void;
  /** Hang up the active call */
  hangUp: () => void;
  /** Toggle mute */
  toggleMute: () => void;
  /** Send DTMF digit */
  sendDigits: (digits: string) => void;
}

export function useTwilioDevice(userId?: string): UseTwilioDeviceReturn {
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [callState, setCallState] = useState<CallState>('idle');
  const [activeCallInfo, setActiveCallInfo] = useState<TwilioCallInfo | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timer for call duration
  const startTimer = useCallback(() => {
    stopTimer();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clean up call event listeners
  const cleanupCall = useCallback(() => {
    callRef.current = null;
    setCallState('idle');
    setActiveCallInfo(null);
    setIsMuted(false);
    stopTimer();
  }, [stopTimer]);

  // Fetch token and initialize/register device
  const initDevice = useCallback(async () => {
    if (!userId) return;

    try {
      setError(null);

      // Use raw fetch (not apiRequest) to avoid triggering auth error counting on 401.
      // This also handles the case where Twilio isn't configured or user isn't authed yet.
      const tokenRes = await fetch('/api/calls/token', { credentials: 'include' });
      if (!tokenRes.ok) {
        if (tokenRes.status === 401) {
          console.log('[TwilioDevice] Not authenticated yet, skipping init');
        } else {
          console.warn('[TwilioDevice] Token request failed:', tokenRes.status);
        }
        return;
      }

      const data = await tokenRes.json();
      const { token, identity } = data;

      console.log('[TwilioDevice] Token received for identity:', identity);

      if (!token) {
        console.warn('[TwilioDevice] No token received');
        setError('Failed to obtain voice token');
        return;
      }

      if (deviceRef.current) {
        // Update token on existing device
        deviceRef.current.updateToken(token);
        return;
      }

      console.log('[TwilioDevice] Initializing new Device with token');
      const device = new Device(token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        logLevel: 1,
        // @ts-ignore - Close protection type definition might be missing in some versions
        closeProtection: 'A call is in progress. Are you sure you want to leave?',
      });

      device.on('registered', () => {
        console.log('[TwilioDevice] Registered successfully');
        setIsReady(true);
        setError(null);
      });

      device.on('error', (err: any) => {
        console.error('[TwilioDevice] Error:', err);
        // Clean up error message
        let msg = err.message || 'Twilio device error';
        if (msg.includes('Identity is required')) {
           msg = 'Configuration error: Invalid token identity';
        }
        setError(msg);
      });

      device.on('tokenWillExpire', async () => {
        console.log('[TwilioDevice] Token expiring, refreshing...');
        try {
          const res = await fetch('/api/calls/token', { credentials: 'include' });
          if (!res.ok) throw new Error(`Token refresh failed: ${res.status}`);
          const { token: newToken } = await res.json();
          device.updateToken(newToken);
        } catch (err) {
          console.error('[TwilioDevice] Token refresh failed:', err);
        }
      });

      device.on('incoming', (call: Call) => {
        console.log('[TwilioDevice] Incoming call from:', call.parameters?.From);
        // For now, auto-reject incoming calls (can be enhanced later)
        call.reject();
      });

      await device.register();
      deviceRef.current = device;
    } catch (err) {
      console.warn('[TwilioDevice] Init failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize calling');
    }
  }, [userId]);

  // Initialize device on mount
  useEffect(() => {
    initDevice();

    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      stopTimer();
    };
  }, [initDevice, stopTimer]);

  // Make an outbound call
  const makeCall = useCallback((params: TwilioCallInfo) => {
    if (!deviceRef.current || !isReady) {
      setError('Phone service not ready. Please try again.');
      return;
    }

    if (callRef.current) {
      setError('A call is already in progress.');
      return;
    }

    setCallState('connecting');
    setActiveCallInfo(params);
    setError(null);

    const connectParams: Record<string, string> = {
      To: params.to,
    };
    if (params.phoneLineId) connectParams.phoneLineId = params.phoneLineId;
    if (params.claimId) connectParams.claimId = params.claimId;
    if (params.contactId) connectParams.contactId = params.contactId;
    if (params.contactName) connectParams.contactName = params.contactName;
    if (userId) connectParams.userId = userId;

    deviceRef.current.connect({ params: connectParams }).then((call: Call) => {
      callRef.current = call;

      call.on('ringing', () => {
        console.log('[TwilioDevice] Call ringing');
        setCallState('ringing');
      });

      call.on('accept', () => {
        console.log('[TwilioDevice] Call accepted/connected');
        setCallState('connected');
        startTimer();
      });

      call.on('disconnect', (call: Call) => {
        console.log('[TwilioDevice] Call disconnected');
        setCallState('disconnected');
        cleanupCall();
      });

      call.on('cancel', () => {
        console.log('[TwilioDevice] Call cancelled');
        setError('Call was cancelled');
        cleanupCall();
      });

      call.on('reject', () => {
        console.log('[TwilioDevice] Call rejected by callee');
        setError('Call was declined');
        cleanupCall();
      });

      call.on('error', (err: any) => {
        console.error('[TwilioDevice] Call error:', err);
        setError(err.message || 'Call failed');
        cleanupCall();
      });

      call.on('warning', (warningName: string) => {
        console.warn('[TwilioDevice] Call warning:', warningName);
      });
    }).catch((err: Error) => {
      console.error('[TwilioDevice] Connect failed:', err);
      setError(err.message || 'Failed to connect call');
      setCallState('idle');
      setActiveCallInfo(null);
    });
  }, [isReady, userId, startTimer, cleanupCall]);

  // Hang up
  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
    cleanupCall();
  }, [cleanupCall]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (callRef.current) {
      const newMuted = !callRef.current.isMuted();
      callRef.current.mute(newMuted);
      setIsMuted(newMuted);
    }
  }, []);

  // Send DTMF digits
  const sendDigits = useCallback((digits: string) => {
    if (callRef.current) {
      callRef.current.sendDigits(digits);
    }
  }, []);

  return {
    isReady,
    callState,
    activeCallInfo,
    callDuration,
    isMuted,
    error,
    makeCall,
    hangUp,
    toggleMute,
    sendDigits,
  };
}
